import { type ReactNode, useCallback, useEffect, useRef } from "react"
import { useGameState } from "./GameStateContext"
import type { ClientboundMessage, ServerboundMessage } from "./protocol.ts"
import { ClientboundMessageSchema } from "./protocol.ts"
import { rejoinStorage } from "./rejoinStorage"
import { WebSocketContext } from "./webSocketContext"
import { WS_URL, HTTP_URL } from "./config"
import { decideClose } from "./wsClose"
import { z } from "zod"

/** Shape of POST /room/create's JSON response (backend CreateRoomResponse). */
const CreateRoomResponseSchema = z.object({
    roomCode: z.string(),
    boxId: z.string(),
    ownerToken: z.string().optional(),
    joinedExisting: z.boolean().optional(),
})

/**
 * Backoff schedule for auto-reconnect attempts. ~90s of trying before we
 * surface a manual-retry UI and stop spamming the server.
 */
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000, 30000, 30000]

/**
 * Cold-start the Railway backend can take >5s. 12s is forgiving enough
 * for cold starts without becoming a UX hole when the server is actually
 * down.
 */
const CONNECT_TIMEOUT_MS = 12000

interface WebSocketProviderProps {
    children: ReactNode
}

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
    const wsRef = useRef<WebSocket | null>(null)

    /**
     * Generation counter — incremented on every openSocket call. Each
     * socket's onclose/onmessage handlers capture their generation at
     * setup time and compare against this on fire; if they don't match,
     * the handler is stale (the socket has been replaced) and bails out.
     * Cleaner than flag-juggling between user-disconnect, reconnect,
     * and natural close.
     */
    const genRef = useRef(0)

    /** How many reconnect attempts we've already made in the current burst.
     *  Reset to 0 on a successful reconnect or a user-initiated connect. */
    const attemptRef = useRef(0)
    const reconnectTimeoutRef = useRef<number | null>(null)

    /** Room code of the socket currently being opened — used at close time to
     *  tell whether a failure pertains to the room our rejoin token is for. */
    const attemptRoomIdRef = useRef<string | null>(null)

    const { set, updateSnapshot, resetGame } = useGameState()

    // Stable refs so the internal handlers below don't need useCallback dep
    // chains. set/updateSnapshot/resetGame are referentially stable in
    // practice (returned from useReducer), but routing through refs makes
    // that explicit and keeps the call sites tidy.
    const setRef = useRef(set); setRef.current = set
    const updateSnapshotRef = useRef(updateSnapshot); updateSnapshotRef.current = updateSnapshot
    const resetGameRef = useRef(resetGame); resetGameRef.current = resetGame

    /* ============================================================
       Internal handlers — these reference each other in a small
       cycle (openSocket → handleClose → scheduleReconnect →
       attemptReconnect → openSocket). Park them in mutable refs so
       each can call the others without React useCallback dep-chasing.
       ============================================================ */

    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            const raw = JSON.parse(event.data) as unknown
            const parsed = ClientboundMessageSchema.safeParse(raw)
            if (!parsed.success) {
                console.error("Invalid message from server:", {
                    raw: event.data,
                    errors: parsed.error.issues,
                })
                return
            }
            const message: ClientboundMessage = parsed.data
            switch (message.type) {
                case "join_room":
                    setRef.current("roomId", message.roomId)
                    rejoinStorage.save(message.roomId, message.rejoinToken)
                    setRef.current("connection", { kind: "connected" })
                    setRef.current("error", null)
                    attemptRef.current = 0
                    break
                case "snapshot":
                    updateSnapshotRef.current(message.phase, message.room, message.self, message.view)
                    break
                case "event":
                    // Transient effect (sound, confetti). State channel is snapshot.
                    if (import.meta.env.DEV) console.log("WS event:", message.eventType, message.data)
                    break
            }
        } catch (error) {
            console.error("Error parsing WS message:", error)
        }
    }, [])

    const attemptReconnectRef = useRef<() => Promise<void>>(async () => {})
    const scheduleReconnectRef = useRef<() => void>(() => {})
    const handleCloseRef = useRef<(e: CloseEvent) => void>(() => {})
    const openSocketRef = useRef<(roomId: string) => Promise<void>>(async () => {})

    scheduleReconnectRef.current = () => {
        if (reconnectTimeoutRef.current != null) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }

        const attempt = attemptRef.current
        if (attempt >= RECONNECT_DELAYS_MS.length) {
            console.warn("Reconnect attempts exhausted")
            setRef.current("connection", { kind: "failed", reason: "We couldn't get back to the room." })
            attemptRef.current = 0
            return
        }

        const delay = RECONNECT_DELAYS_MS[attempt]
        setRef.current("connection", { kind: "reconnecting", attempt: attempt + 1 })

        reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = null
            void attemptReconnectRef.current()
        }, delay)
    }

    attemptReconnectRef.current = async () => {
        attemptRef.current += 1
        const stored = rejoinStorage.get()
        if (!stored) {
            setRef.current("connection", { kind: "failed", reason: "Lost our place in the room." })
            return
        }

        try {
            await openSocketRef.current(stored.roomId)
            // Socket is open — send the rejoin straight away. The clientbound
            // join_room handler will flip us to "connected" once it arrives.
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "rejoin_room", rejoinToken: stored.token }))
            }
        } catch (e) {
            console.warn("Reconnect attempt failed:", e)
            // openSocket's close handler may have already routed through
            // handleClose for a terminal code (4006 invalid token, etc.) and
            // set the connection to failed/kicked. We only schedule another
            // attempt if we're still in a retryable state.
            scheduleReconnectRef.current()
        }
    }

    handleCloseRef.current = (event: CloseEvent) => {
        const { code, reason } = event
        // Policy (which codes are terminal vs retryable) lives in decideClose,
        // unit-tested in wsClose.test.ts; here we just apply the decision.
        const stored = rejoinStorage.get()
        const decision = decideClose(code, reason, {
            hasRejoin: stored != null,
            isStoredRoom: stored != null && stored.roomId === attemptRoomIdRef.current,
        })

        if (decision.clearRejoin) rejoinStorage.clear()
        if (decision.reset) resetGameRef.current()
        if (decision.error !== undefined) setRef.current("error", decision.error)

        if (decision.reconnect) {
            console.warn(`WS closed (${code} ${reason}) — starting reconnect loop`)
            scheduleReconnectRef.current()
            return
        }
        if (decision.connection) setRef.current("connection", decision.connection)
    }

    openSocketRef.current = (roomId: string): Promise<void> => {
        attemptRoomIdRef.current = roomId
        return new Promise((resolve, reject) => {
            // Tear down any existing socket without letting its onclose
            // re-trigger our retry logic. Bumping the generation invalidates
            // any pending events from the old socket.
            if (wsRef.current) {
                const old = wsRef.current
                wsRef.current = null
                genRef.current++
                try { old.close(1000) } catch { /* no-op */ }
            }
            const myGen = ++genRef.current

            let socket: WebSocket
            try {
                socket = new WebSocket(`${WS_URL}/room/${roomId}`)
            } catch (e) {
                reject(e instanceof Error ? e : new Error("Failed to open WebSocket"))
                return
            }

            const failOpen = (message: string) => {
                if (myGen !== genRef.current) return
                setRef.current("error", message)
                setRef.current("connection", { kind: "failed", reason: message })
            }

            const timeoutId = window.setTimeout(() => {
                if (socket.readyState !== WebSocket.OPEN) {
                    try { socket.close() } catch { /* no-op */ }
                    failOpen("Couldn't reach the server. Check your connection and try again.")
                    reject(new Error("Connect timeout"))
                }
            }, CONNECT_TIMEOUT_MS)

            const cleanupStartup = () => {
                clearTimeout(timeoutId)
                socket.removeEventListener("open", onOpen)
                socket.removeEventListener("error", onErrorStartup)
                socket.removeEventListener("close", onCloseStartup)
            }

            const onOpen = () => {
                cleanupStartup()
                if (myGen !== genRef.current) {
                    // We got superseded mid-open. Drop the socket and bail.
                    try { socket.close() } catch { /* no-op */ }
                    reject(new Error("Generation changed mid-open"))
                    return
                }
                wsRef.current = socket

                socket.onmessage = handleMessage
                socket.onerror = (e) => {
                    if (myGen === genRef.current) console.error("WS runtime error:", e)
                }
                socket.onclose = (event) => {
                    if (myGen !== genRef.current) return
                    if (wsRef.current === socket) wsRef.current = null
                    handleCloseRef.current(event)
                }

                resolve()
            }

            const onErrorStartup = (e: Event) => {
                cleanupStartup()
                console.error("WS open error:", e)
                failOpen("Couldn't reach the server. Check your connection and try again.")
                reject(new Error("Connection error during open"))
            }

            const onCloseStartup = (event: CloseEvent) => {
                cleanupStartup()
                // Route through the regular close handler so terminal codes
                // (room-not-found, invalid-name, etc.) get the same state
                // transitions whether they arrive before or after open.
                if (myGen === genRef.current) {
                    handleCloseRef.current(event)
                }
                reject(new Error(`Closed during open: ${event.code} ${event.reason}`))
            }

            socket.addEventListener("open", onOpen)
            socket.addEventListener("error", onErrorStartup)
            socket.addEventListener("close", onCloseStartup)
        })
    }

    /* ============================================================
       Public API — stable references via useCallback so consumers
       can pass them to effect dep arrays without churn.
       ============================================================ */

    const sendMessage = useCallback((message: ServerboundMessage) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message))
        } else {
            console.warn("Dropped message — WS not open:", message)
        }
    }, [])

    const connect = useCallback(async (roomId: string): Promise<void> => {
        if (reconnectTimeoutRef.current != null) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        attemptRef.current = 0
        setRef.current("connection", { kind: "connecting" })
        setRef.current("error", null)
        await openSocketRef.current(roomId)
        // Note: connection state flips to "connected" when the clientbound
        // join_room handler runs. The caller is responsible for sending
        // join_room / rejoin_room next.
    }, [])

    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current != null) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        // Bump generation so the impending onclose is treated as stale.
        genRef.current++
        if (wsRef.current) {
            try { wsRef.current.close(1000) } catch { /* no-op */ }
            wsRef.current = null
        }
        rejoinStorage.clear()
        attemptRef.current = 0
        setRef.current("connection", { kind: "idle" })
    }, [])

    const createRoom = useCallback(async (
        params?: { boxId: string, p?: string, c?: string, ownerToken?: string }
    ): Promise<{ roomCode: string, joinedExisting: boolean }> => {
        const response = await fetch(`${HTTP_URL}/room/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params || { boxId: 'default' })
        })

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error("Authentication failed. Please scan your box again.")
            }
            throw new Error(`Failed to create room: ${response.statusText}`)
        }

        const data = CreateRoomResponseSchema.parse(await response.json())

        if (data.ownerToken) {
            localStorage.setItem("ownerToken", data.ownerToken)
            localStorage.setItem("ownedBoxId", data.boxId)
        }

        return { roomCode: data.roomCode, joinedExisting: !!data.joinedExisting }
    }, [])

    const rejoin = useCallback(async (): Promise<boolean> => {
        const stored = rejoinStorage.get()
        if (!stored) return false
        try {
            await connect(stored.roomId)
            sendMessage({ type: "rejoin_room", rejoinToken: stored.token })
            return true
        } catch (e) {
            console.error("Manual rejoin failed:", e)
            return false
        }
    }, [connect, sendMessage])

    const retryReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current != null) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        attemptRef.current = 0
        setRef.current("error", null)
        void attemptReconnectRef.current()
    }, [])

    const hasRejoinState = useCallback(() => rejoinStorage.exists(), [])

    /* ============================================================
       visibilitychange + online: shortcut the backoff.
       If we're disconnected and the device just came back online (or
       the tab returned to the foreground), don't wait through the
       backoff schedule — reset and try immediately.
       ============================================================ */

    useEffect(() => {
        const wake = () => {
            if (!rejoinStorage.exists()) return
            if (wsRef.current?.readyState === WebSocket.OPEN) return
            if (reconnectTimeoutRef.current != null) {
                clearTimeout(reconnectTimeoutRef.current)
                reconnectTimeoutRef.current = null
            }
            attemptRef.current = 0
            void attemptReconnectRef.current()
        }
        const onVisible = () => {
            if (document.visibilityState === "visible") wake()
        }
        window.addEventListener("online", wake)
        document.addEventListener("visibilitychange", onVisible)
        return () => {
            window.removeEventListener("online", wake)
            document.removeEventListener("visibilitychange", onVisible)
        }
    }, [])

    /* Polite close on page unload so the server sees a clean 1000 instead of an
     * abnormal 1006.
     *
     * The generation bump is load-bearing, not tidiness. A 1000 close means "the
     * room is gone" — the server sends it from Game.shutdown when a room is
     * reaped or removed — so decideClose treats it as terminal and drops the
     * rejoin token. Closing our own socket with 1000 here therefore had us
     * deliver that verdict to ourselves on every refresh, deleting the token the
     * refresh existed to use: reload mid-game and the "Welcome back" prompt was
     * already gone by the time the home screen mounted. Bumping the generation
     * marks the socket stale, which is the same mechanism disconnect() uses, so
     * the close lands with nobody listening. The page is going away; there is no
     * decision left to make. */
    useEffect(() => {
        const onUnload = () => {
            genRef.current++
            try { wsRef.current?.close(1000) } catch { /* no-op */ }
            wsRef.current = null
        }
        window.addEventListener("beforeunload", onUnload)
        return () => window.removeEventListener("beforeunload", onUnload)
    }, [])

    const value = {
        connect,
        disconnect,
        createRoom,
        sendMessage,
        rejoin,
        retryReconnect,
        hasRejoinState,
    }

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    )
}
